function positive(value: number, name: string): number {
  if (!Number.isFinite(value) || value <= 0) throw new RangeError(`${name} must be positive`);
  return value;
}

export function secondsPerSixteenth(bpm: number, sixteenthsPerBeat: number): number {
  return 60 / positive(bpm, 'bpm') / positive(sixteenthsPerBeat, 'sixteenthsPerBeat');
}

export function toneTransportBpm(bpm: number, sixteenthsPerBeat: number): number {
  return positive(bpm, 'bpm') * positive(sixteenthsPerBeat, 'sixteenthsPerBeat') / 4;
}

export function barsDurationSeconds(
  bars: number,
  bpm: number,
  sixteenthsPerBeat: number,
  sixteenthsPerBar: number,
): number {
  if (!Number.isFinite(bars) || bars < 0) throw new RangeError('bars must not be negative');
  return bars * positive(sixteenthsPerBar, 'sixteenthsPerBar')
    * secondsPerSixteenth(bpm, sixteenthsPerBeat);
}

export function hitDurationSeconds(
  hitPositions: Iterable<number>,
  currentSixteenth: number,
  secondsPerStep: number,
  sixteenthsPerBar: number,
  staccato = 0.9,
): number {
  const barEnd = positive(sixteenthsPerBar, 'sixteenthsPerBar');
  positive(secondsPerStep, 'secondsPerStep');
  positive(staccato, 'staccato');
  let next = barEnd;
  for (const position of [...hitPositions].sort((a, b) => a - b)) {
    if (position > currentSixteenth) {
      next = position;
      break;
    }
  }
  if (currentSixteenth < 0 || currentSixteenth >= next) {
    throw new RangeError('currentSixteenth must precede the next hit or bar boundary');
  }
  return (next - currentSixteenth) * secondsPerStep * staccato;
}

export function beatOffsetSeconds(
  hitTime: number,
  anchorTime: number,
  beatSeconds: number,
): number {
  if (!Number.isFinite(beatSeconds) || beatSeconds <= 0) {
    throw new RangeError('beatSeconds must be positive');
  }
  const wrapped = ((hitTime - anchorTime) % beatSeconds + beatSeconds) % beatSeconds;
  return wrapped > beatSeconds / 2 ? wrapped - beatSeconds : wrapped;
}

// Anchor of the beat grid that play-along timing is measured against, for a
// step the Tone.js transport schedules. Only beats on an even eighth count:
// Tone swings every second eighth, so in 6/8 the second beat (step 6) may be
// shifted, while the bar downbeat never is. Off-beat steps keep the last anchor.
export function grooveBeatAnchor(input: {
  step: number;
  sixteenthsPerBeat: number;
  secondsPerSixteenth: number;
  scheduledTime: number;
}): { time: number; beatSec: number } | null {
  const sixteenthsPerBeat = positive(input.sixteenthsPerBeat, 'sixteenthsPerBeat');
  const secondsPerStep = positive(input.secondsPerSixteenth, 'secondsPerSixteenth');
  if (!Number.isInteger(input.step) || input.step < 0) {
    throw new RangeError('step must be a non-negative integer');
  }
  if (!Number.isFinite(input.scheduledTime)) throw new RangeError('scheduledTime must be finite');
  if (input.step % sixteenthsPerBeat !== 0 || input.step % 4 !== 0) return null;
  return { time: input.scheduledTime, beatSec: secondsPerStep * sixteenthsPerBeat };
}

export function runStamps(input: {
  flawless: boolean;
  meanAbsOffsetMs: number | null;
  tightMs?: number;
  looseMs?: number;
}): 0 | 1 | 2 | 3 {
  if (!input.flawless) return 0;
  if (input.meanAbsOffsetMs == null) return 1;
  const tightMs = input.tightMs ?? 60;
  const looseMs = input.looseMs ?? 120;
  if (input.meanAbsOffsetMs <= tightMs) return 3;
  if (input.meanAbsOffsetMs <= looseMs) return 2;
  return 1;
}
